import { ButtonFloatingSubmit, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import usePatientMutation from "@/hooks/usePatientMutation";
import { CreateCaseExisitingPatientPayloadSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import TextField from "@mui/material/TextField";
import { createFileRoute } from "@tanstack/react-router";
import omit from "lodash/omit";
import { Controller, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";

export const Route = createFileRoute("/dashboard/case/create/exist/")({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const {
		control,
		setError,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<CreateCaseExisitingPatientPayloadSchema>({
		mode: "all",
		resolver: zodResolver(CreateCaseExisitingPatientPayloadSchema),
		defaultValues: {
			patient_ic_number: "",
		},
	});

	const { check } = usePatientMutation();

	const onSubmit: SubmitHandler<
		CreateCaseExisitingPatientPayloadSchema
	> = async (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		try {
			await check.mutateAsync(data.patient_ic_number);
			return navigate({
				to: "/dashboard/case/create/exist/patient/$patientIc",
				params: {
					patientIc: data.patient_ic_number,
				},
			});
		} catch (err) {
			setError("patient_ic_number", {
				type: "validate",
				message: `Patient with NRIC/IC ${data.patient_ic_number} not found`,
			});
		}
	};

	return (
		<div className="relative h-full bg-white flex flex-col">
			<BackAppBar />
			<div className="container flex flex-col flex-1">
				<Typography fontWeight={600} fontSize={28}>
					Select existing patient
				</Typography>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-1 flex-col gap-6 py-4"
				>
					<Controller
						control={control}
						name="patient_ic_number"
						defaultValue=""
						render={({ field, fieldState }) => (
							<TextField
								id={field.name}
								label="Patient’s ID"
								placeholder="Enter Patient's ID"
								className="[&_.MuiInputBase-root]:!rounded-xl"
								error={!!fieldState.error?.message}
								helperText={fieldState.error?.message}
								{...omit(field, ["ref"])}
							/>
						)}
					/>
					<ButtonFloatingSubmit
						type="submit"
						label="Submit"
						disabled={!isValid || !isDirty || isSubmitting}
					/>
				</form>
			</div>
		</div>
	);
}
